import React, { useState } from "react";
import { Mic } from "lucide-react";
import { useGetAudioClipsQuery } from "@/store/rest_api";
import AudioClipLibrary from "@/components/audio/AudioClipLibrary";
import { AudioLibrary } from "@/enum/AudioLibrary";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const NONE_VALUE = "__none__";

interface AudioClipPickerProps {
  value?: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  category?: AudioLibrary;
  valueKey?: "url" | "id";
}

export default function AudioClipPicker({
  value,
  onChange,
  placeholder,
  category = AudioLibrary.AUDIO_CLIP,
  valueKey = "url",
}: AudioClipPickerProps) {
  const { data: clips = [] } = useGetAudioClipsQuery(category);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  const isKnownClip = clips.some((clip) => clip[valueKey] === value);
  const hasCustomValue = Boolean(value) && !isKnownClip && valueKey === "url";
  const isVoicemailLibrary = category === AudioLibrary.VOICEMAIL_MESSAGE;

  return (
    <div className="flex gap-2">
      <div className="flex-1">
        <Select
          value={value && (isKnownClip || hasCustomValue) ? value : NONE_VALUE}
          onValueChange={(selected) => onChange(selected === NONE_VALUE ? null : selected)}
        >
          <SelectTrigger>
            <SelectValue placeholder={placeholder ?? "None"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>{placeholder ?? "None"}</SelectItem>
            {clips.map((clip) => (
              <SelectItem key={clip.id} value={clip[valueKey]}>
                {clip.name}
              </SelectItem>
            ))}
            {hasCustomValue && <SelectItem value={value!}>Custom audio URL</SelectItem>}
          </SelectContent>
        </Select>
      </div>

      <Button type="button" variant="outline" onClick={() => setIsLibraryOpen(true)}>
        <Mic className="mr-2 h-4 w-4" />
        Record or upload
      </Button>

      <Dialog open={isLibraryOpen} onOpenChange={setIsLibraryOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isVoicemailLibrary ? "Voicemail messages" : "Audio clips"}</DialogTitle>
            <DialogDescription>
              Record a new {isVoicemailLibrary ? "message" : "clip"}, upload an MP3 or WAV, create one from text, or pick
              one from your library.
            </DialogDescription>
          </DialogHeader>
          <AudioClipLibrary
            category={category}
            onSelect={(clip) => {
              onChange(clip[valueKey]);
              setIsLibraryOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
